import { Link } from "react-router";
import btIconEmail from "@assets/icon-bt-email.svg";
import btIconX from "@assets/icon-bt-x.svg";
import ImagePlaceholder from "@components/ArticleCard/components/ImagePlaceholder";
import OptimisedImage from "@components/OptimisedImage";
import { IS_PLAYWRIGHT } from "@util/constant";
import cx from "classnames";
import { isEmpty } from "lodash-es";

type AuthorDetailsProps = {
  /** The headshot image of the author. */
  headshotImage?: string;
  /** The name of the author. */
  name: string;
  /** The designation of the author. */
  designation: string;
  /** The email of the author. */
  email: string;
  /** The Twitter handle of the author. */
  twitterHandle: string;
  /** The raw bio of the author. */
  bio_raw: string;
  /** The URL path of the author. */
  urlPath: string;
};

const AuthorDetails = ({
  headshotImage,
  name,
  designation,
  email,
  twitterHandle,
  bio_raw,
}: AuthorDetailsProps): React.ReactElement => {
  return (
    <div
      className={cx(
        "mb-8 flex flex-col gap-4 border-b-[5px] border-black pb-8 font-poppins",
        "md:flex-row"
      )}
    >
      {headshotImage ? (
        <div className="w-full md:w-4/20">
          {!IS_PLAYWRIGHT ? (
            <OptimisedImage
              src={headshotImage}
              filters={[
                {
                  breakpoint: "(min-width: 576px)",
                  w: 300,
                  h: 300,
                  dpr: 1,
                  f: "webp",
                },
                { w: 100, h: 100, dpr: 1, f: "webp" },
              ]}
              alt={name}
              className="mx-auto aspect-square w-[100px] rounded-full object-cover md:w-[174px]"
            />
          ) : (
            <ImagePlaceholder
              aspectRatio="aspect-square"
              className="mx-auto aspect-square w-[100px] max-w-full rounded-full md:w-[174px]"
            />
          )}
        </div>
      ) : null}

      <div className="flex w-full flex-col md:w-16/20">
        <h1 className="text-center font-poppins text-xl font-semibold md:text-start">
          {name}
        </h1>

        {!isEmpty(designation) ? (
          <p className="mb-2 text-center font-poppins text-4xs font-medium text-gray-850 md:text-start">
            {designation.toUpperCase()}
          </p>
        ) : null}

        {!isEmpty(bio_raw) ? (
          <p className="mb-3 font-public-sans text-xs text-gray-850">
            {bio_raw}
          </p>
        ) : null}

        {!isEmpty(email) || !isEmpty(twitterHandle) ? (
          <div
            className={cx(
              "mt-2 flex gap-2 border-t border-gray-850 pt-3",
              "md:gap-5"
            )}
          >
            {!isEmpty(email) ? (
              <div className="flex items-center">
                <img src={btIconEmail} height={15} width={15} alt="email" />

                <Link
                  to={`mailto:${email}`}
                  className="ml-2 font-public-sans text-4xs text-gray-550 transition-colors hover:text-black hover:underline"
                >
                  {email}
                </Link>
              </div>
            ) : null}

            {!isEmpty(twitterHandle) ? (
              <div className="flex items-center">
                <img src={btIconX} height={15} width={15} alt="email" />

                <Link
                  to={`https://x.com/${twitterHandle.replace("@", "")}`}
                  className="ml-1 font-public-sans text-4xs text-gray-550 transition-colors hover:text-black hover:underline"
                  target="_blank"
                >
                  {twitterHandle}
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AuthorDetails;
